import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { IdentityModel } from '@core/models/identity.model';
import { InviteModel } from '@core/models/invite.model';
import * as fromRoot from '@core/redux';
import * as InviteActions from '@core/redux/invite/invite.actions';
import { InviteActionTypes } from '@core/redux/invite/invite.actions';
import { InviteService } from '@core/services/invite.service';
import { Validation } from '@core/validation/validation.abstract.class';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AvatarComponent } from '@ux/components/avatar/avatar.component';
import { SelectOnlyComponent } from '@ux/components/select/only/select-only/select.component';
import { SelectOnlyHeaderComponent } from '@ux/components/select/only/select-only-header/select-only-header.component';
import { SelectOnlyItemsComponent } from '@ux/components/select/only/select-only-items/select-only-items.component';

type InviteRole = 'owner' | 'admin' | 'retail' | 'user';

interface InviteFormData {
  email: string | null;
  role: InviteRole | null;
}

interface InviteForm {
  email: FormControl<string | null>;
  role: FormControl<InviteRole | null>;
}

@Component({
  selector: 'app-setting-invite',
  templateUrl: './setting-invite.component.html',
  styleUrls: ['./setting-invite.component.less'],
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    SelectOnlyComponent,
    SelectOnlyHeaderComponent,
    SelectOnlyItemsComponent,
    CommonModule,
    AvatarComponent,
  ],
  providers: [InviteService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingInviteComponent extends Validation implements OnInit {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly updates = inject(Actions);
  private readonly formBuilder = inject(FormBuilder);
  private readonly inviteService = inject(InviteService);

  public readonly roles = this.inviteService.findAllRoles();
  public override form = this.initForm();

  // Store selectors as signals
  private readonly invites = toSignal(this.store.select(fromRoot.getInviteNoDeleteEntities), {
    initialValue: [] as InviteModel[],
  });

  protected readonly user = toSignal(this.store.select(fromRoot.getUser), {
    initialValue: null as IdentityModel | null,
  });

  protected readonly isLoadingForm = toSignal(this.store.select(fromRoot.getInviteLoading), { initialValue: false });

  // Computed signals
  protected readonly activeInvite = computed(() => this.invites().filter(invite => invite.status === 'active'));

  protected readonly notActiveInvite = computed(() => this.invites().filter(invite => invite.status === 'noActive'));

  // Handle validation errors with computed
  protected readonly errors = toSignal(this.store.select(fromRoot.getInviteErrors), { initialValue: null });

  protected readonly computedValidateErrors = computed(() => {
    const currentErrors = this.errors();
    if (currentErrors) {
      this.validateErrors = currentErrors;
    }
    return currentErrors;
  });

  // Handle actions with computed
  private readonly createSuccessSignal = toSignal(this.updates.pipe(ofType(InviteActionTypes.CreateSuccessAction)), {
    initialValue: null,
  });

  protected readonly handleCreateSuccess = computed(() => {
    const success = this.createSuccessSignal();
    if (success) {
      const defaultRole = this.roles[0]?.id as InviteRole;
      this.form.reset({
        email: null,
        role: defaultRole,
      });
    }
    return success;
  });

  private readonly resendFailureSignal = toSignal(
    this.updates.pipe(ofType(InviteActionTypes.ResendEmailFailureAction)),
    { initialValue: null }
  );

  protected readonly handleResendFailure = computed(() => {
    const failure = this.resendFailureSignal();
    if (failure) {
      alert('Ошибка повторной отправки приглашения');
    }
    return failure;
  });

  constructor() {
    super();
  }

  ngOnInit(): void {}

  private initForm(): FormGroup<InviteForm> {
    const defaultRole = this.roles[0]?.id as InviteRole;

    return this.formBuilder.group<InviteForm>({
      email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
      role: new FormControl<InviteRole | null>(defaultRole, [Validators.required]),
    });
  }

  protected onSubmit(): void {
    if (this.form.valid) {
      const formData = this.form.getRawValue() as InviteFormData;
      const defaultRole = this.roles[0]?.id as InviteRole;

      const email = formData.email || '';
      const role = formData.role || defaultRole;

      // The backend will handle generating the ID and other required fields
      this.store.dispatch(
        new InviteActions.CreateAction({
          email,
          role,
          status: 'noActive',
          user: { email },
        } as unknown as InviteModel)
      );
    }
  }

  protected onResend(id: string): void {
    this.store.dispatch(new InviteActions.ResendEmailAction(id));
  }

  protected onDelete(id: string): void {
    this.store.dispatch(new InviteActions.DeleteAction(id));
  }

  protected onChangeRole(invite: InviteModel): void {
    this.store.dispatch(new InviteActions.SaveAction(invite));
  }

  protected handleRoleChange(invite: InviteModel, newRole: InviteRole): void {
    const updatedInvite = Object.assign(new InviteModel(), invite, { role: newRole });
    this.onChangeRole(updatedInvite);
  }
}
